"use client";

import { useEffect, useMemo, useState, useCallback, useRef } from "react";

import type { CreatorProfileForMyLink } from "@artha/types";
import type { JobFiltersResponseDto } from "@artha/api-contracts";
import type { JobFiltersApiResponse, JobFiltersState } from "@artha/ui";
import {
  buildJobIndexParams,
  buildPublicJobParams,
  useOptimizedJobCount,
} from "@/modules/common/services/jobApi";

const defaultFiltersState: JobFiltersState = {
  categories: [],
  jobTypes: [],
  experienceLevels: [],
  educationLevels: [],
  workModes: [],
  country: "",
  state: "",
  city: "",
  salaryRanges: [],
  companies: [],
  industries: [],
  companyRating: 0,
};

type UseOptimizedJobFiltersStateParams = {
  creator?: CreatorProfileForMyLink | null;
  filters?: JobFiltersResponseDto | null;
  nicheFallback?: string[];
  enableJobCount?: boolean;
  isPublicProfile?: boolean; // New flag to indicate if this is for a public profile
};

/**
 * Optimized version of useJobFiltersState with smart job count fetching
 * Features:
 * - Debounced job count requests
 * - Request deduplication
 * - Background updates
 */
/**
 * Optimized job filters state hook (replaces useJobFiltersState)
 * Features:
 * - Debounced job count requests
 * - Request deduplication
 * - Background updates
 */
export const useJobFiltersState = ({
  creator,
  filters,
  nicheFallback,
  enableJobCount,
  isPublicProfile,
}: UseOptimizedJobFiltersStateParams) => {
  return useOptimizedJobFiltersState({
    creator,
    filters,
    nicheFallback,
    enableJobCount: enableJobCount ?? false, // Keep original behavior by default
    isPublicProfile,
  });
};

export const useOptimizedJobFiltersState = ({
  creator,
  filters,
  nicheFallback,
  enableJobCount = false,
  isPublicProfile = false,
}: UseOptimizedJobFiltersStateParams) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const jobFeedPreferences = creator?.settings?.jobFeedPreferences;
  const preferenceFilterState = useMemo(() => {
    if (!jobFeedPreferences) return undefined;
    const hasItems = (value?: string[]) => Array.isArray(value) && value.length > 0;
    const built: Partial<JobFiltersState> = {};
    if (hasItems(jobFeedPreferences.categories)) {
      built.categories = jobFeedPreferences.categories!;
    }
    if (hasItems(jobFeedPreferences.jobTypes)) {
      built.jobTypes = jobFeedPreferences.jobTypes!;
    }
    if (hasItems(jobFeedPreferences.workModes)) {
      built.workModes = jobFeedPreferences.workModes!;
    }
    if (hasItems(jobFeedPreferences.experienceLevels)) {
      built.experienceLevels = jobFeedPreferences.experienceLevels!;
    }
    if (hasItems(jobFeedPreferences.educationLevels)) {
      built.educationLevels = jobFeedPreferences.educationLevels!;
    }
    return Object.keys(built).length ? built : undefined;
  }, [jobFeedPreferences]);

  // Seed preferences synchronously when available on first render so the
  // request params (and query key) are stable from the start — applying them
  // in an effect caused an extra jobs fetch after mount.
  const canSeedPreferences = Boolean(filters && preferenceFilterState);
  const [appliedFilters, setAppliedFilters] = useState<JobFiltersState>(() =>
    canSeedPreferences
      ? { ...defaultFiltersState, ...preferenceFilterState }
      : defaultFiltersState
  );
  const [draftFilters, setDraftFilters] = useState<JobFiltersState>(() =>
    canSeedPreferences
      ? { ...defaultFiltersState, ...preferenceFilterState }
      : defaultFiltersState
  );
  const preferencesAppliedRef = useRef(canSeedPreferences);

  useEffect(() => {
    if (isFiltersOpen) {
      setDraftFilters(appliedFilters);
    }
  }, [appliedFilters, isFiltersOpen]);

  const filterParams = useMemo(
    () => {
      const userFilters = {
        categories: appliedFilters.categories,
        job_type: appliedFilters.jobTypes,
        work_mode: appliedFilters.workModes,
        education: appliedFilters.educationLevels,
        exp_level: appliedFilters.experienceLevels,
        location: appliedFilters.country ? [appliedFilters.country] : undefined,
      };

      // Use appropriate parameter builder based on context
      if (isPublicProfile) {
        return buildPublicJobParams({
          creator,
          userFilters,
        });
      } else {
        return buildJobIndexParams({
          creator,
          params: userFilters,
        });
      }
    },
    [appliedFilters, creator, isPublicProfile]
  );

  // Optimized job count query - ensure categories is always provided
  const jobCountParams = useMemo(() => ({
    ...filterParams,
    categories: filterParams.categories || "software-development", // Ensure categories is always a string
  }), [filterParams]);

  const {
    data: jobCountData,
    isLoading: isLoadingJobCount,
    debouncedRefetch: refetchJobCount,
  } = useOptimizedJobCount(
    jobCountParams,
    {
      enabled: enableJobCount && !!appliedFilters.categories.length,
      debounceMs: 300, // Faster debounce for filter changes
    }
  );

  const locationParams = useMemo(
    () => ({
      location: appliedFilters.country || undefined,
      state: appliedFilters.state || undefined,
      city: appliedFilters.city || undefined,
    }),
    [appliedFilters.city, appliedFilters.country, appliedFilters.state]
  );

  const salaryParams = useMemo(() => {
    if (!appliedFilters.salaryRanges.length) return {};
    const value = appliedFilters.salaryRanges[0];
    const parts = value.match(/\d+/g);
    if (!parts || parts.length < 2) return {};
    return {
      salary_min: Number(parts[0]),
      salary_max: Number(parts[1]),
    };
  }, [appliedFilters.salaryRanges]);

  const categoriesParam = useMemo(() => {
    // For public profiles, always use nicheFallback (slugs from settings.nicheCategories)
    // instead of appliedFilters.categories (display names from filter UI)
    if (isPublicProfile) {
      return nicheFallback?.length ? nicheFallback : undefined;
    }
    // For authenticated creator (my-link), use applied filters or fallback
    const categoryList =
      appliedFilters.categories.length > 0
        ? appliedFilters.categories
        : nicheFallback ?? [];
    return categoryList.length ? categoryList : undefined;
  }, [appliedFilters.categories, nicheFallback, isPublicProfile]);

  const derivedFilterParams = useMemo(
    () => ({
      ...filterParams,
      industry: appliedFilters.industries[0],
      company: appliedFilters.companies[0],
    }),
    [appliedFilters.companies, appliedFilters.industries, filterParams]
  );

  const publicFilterParams = useMemo(() => {
    const { categories, ...rest } = derivedFilterParams;
    return rest;
  }, [derivedFilterParams]);

  const requestParams = useMemo(
    () => ({
      categories: categoriesParam,
      niche_keywords: derivedFilterParams.niche_keywords,
      ...locationParams,
      ...salaryParams,
      ...publicFilterParams,
    }),
    [
      categoriesParam,
      derivedFilterParams.niche_keywords,
      locationParams,
      publicFilterParams,
      salaryParams,
    ]
  );

  const requestParamsKey = useMemo(
    () => JSON.stringify(requestParams),
    [requestParams]
  );

  // True only when filters differ from the auto-applied baseline
  // (defaults, or defaults + creator preferences) — i.e. the user changed them.
  const hasUserModifiedFilters = useMemo(() => {
    const appliedKey = JSON.stringify(appliedFilters);
    if (appliedKey === JSON.stringify(defaultFiltersState)) return false;
    if (preferenceFilterState) {
      return (
        appliedKey !==
        JSON.stringify({ ...defaultFiltersState, ...preferenceFilterState })
      );
    }
    return true;
  }, [appliedFilters, preferenceFilterState]);

  useEffect(() => {
    if (!filters || !preferenceFilterState) return;
    if (preferencesAppliedRef.current) return;
    preferencesAppliedRef.current = true;
    setAppliedFilters((prev) => ({
      ...prev,
      ...preferenceFilterState,
    }));
    setDraftFilters((prev) => ({
      ...prev,
      ...preferenceFilterState,
    }));
  }, [filters, preferenceFilterState]);

  // Optimized filter change handler with job count refetch
  const handleFiltersApply = useCallback((newFilters: JobFiltersState) => {
    setAppliedFilters(newFilters);
    setIsFiltersOpen(false);
    
    // Trigger optimized job count refetch if enabled
    if (enableJobCount && newFilters.categories.length > 0) {
      refetchJobCount();
    }
  }, [enableJobCount, refetchJobCount]);

  const handleFiltersReset = useCallback((next?: JobFiltersState) => {
    const resetState = next ?? defaultFiltersState;
    setAppliedFilters(resetState);
    setDraftFilters(resetState);
    setIsFiltersOpen(false);
  }, []);

  const filtersForSheet = useMemo<JobFiltersApiResponse | undefined>(() => {
    if (!filters) return undefined;
    const adaptedFilters: JobFiltersApiResponse = {
      categories: filters.categories ?? [],
      job_types: filters.job_types ?? [],
      experience_levels: (filters.experience_levels ?? []).map((option) => ({
        ...option,
        max_years: option.max_years ?? null,
      })),
      education_levels: filters.education_levels ?? [],
      work_modes: filters.work_modes ?? [],
      countries: filters.countries ?? [],
      states: filters.states ?? [],
      cities: filters.cities ?? [],
      companies: filters.companies ?? [],
      industries: filters.industries ?? [],
      salary_ranges: filters.salary_ranges ?? null,
    };

    if (nicheFallback?.length) {
      adaptedFilters.categories = nicheFallback.map((tag) => ({
        value: tag,
        count: 0,
      }));
    }

    return adaptedFilters;
  }, [filters, nicheFallback]);

  return {
    appliedFilters,
    setAppliedFilters,
    draftFilters,
    setDraftFilters,
    isFiltersOpen,
    setIsFiltersOpen,
    filtersForSheet,
    handleFiltersReset,
    handleFiltersApply,
    requestParams,
    requestParamsKey,
    preferenceFilterState,
    hasUserModifiedFilters,
    // Optimized job count data
    jobCount: jobCountData?.count,
    isLoadingJobCount,
    refetchJobCount,
  };
};
